/**
 * Create the store with asynchronously loaded reducers
 */
import { BehaviorSubject } from 'rxjs';
import { createStore, applyMiddleware, compose } from 'redux';
import { persistStore } from 'redux-persist-immutable';
import { asyncLocalStorage } from 'redux-persist/storages';
import { fromJS } from 'immutable';
import { createEpicMiddleware, combineEpics } from 'redux-observable';
import { routerMiddleware } from 'connected-react-router/immutable';
import * as Sentry from '@sentry/react';
// import { serialize } from 'json-immutable';

import createFilter from 'utils/createFilter';
import Auth from 'entities/auth/model';
// import { redactData } from 'redactData';
import { sentryReduxMiddleware } from 'utils/sentryReduxMiddleware';

import metricsMiddleware from './middleware/metrics';
import config from './config';
import createReducer, { MutableState } from './reducers';
import globalEpics from './epics';

const REDUX_FORM_SUBMIT_FAILED_ACTION = '@@redux-form/SET_SUBMIT_FAILED';

declare global {
  interface Window {
    dumpRedactedReduxState?: () => string;
    getReduxState?: () => MutableState;
    copy?: (something: unknown) => void;
  }
}

const epic$ = new BehaviorSubject(combineEpics(...globalEpics));
const rootEpic = (action$, $store) => epic$.mergeMap((epic) => epic(action$, $store));

if (process.env.NODE_ENV === 'production') {
  Sentry.init({
    release: process.env.COMMIT_HASH,
    dsn: 'https://43f7995df22e48bcb4c12c37d5c1713e@o161227.ingest.sentry.io/1231423',
    ignoreErrors: [
      'Loading chunk',
      'Failed to load https://www.googletagmanager.com/gtag/',
      /Non-Error promise rejection captured with value: Object Not Found Matching Id:/,
    ],
    environment: config.environment,
    integrations: [
      Sentry.rewriteFramesIntegration({
        iteratee: (frame: any) => ({
          ...frame,
          filename: `~${frame?.filename?.substring(frame?.filename?.lastIndexOf('/'))}`,
        }),
      }),
    ],
  });
}

const epicMiddleware = createEpicMiddleware(rootEpic);

const logActions = () => (next) => (action) => {
  if (process.env.NODE_ENV !== 'production') {
    // console.log(action);
  }
  next(action);
};

const logErrors = () => (next) => (action) => {
  if (action.type && action.type.indexOf('FAILED') !== -1) {
    const { error: maybeError, errorCode, type, ...rest } = action;
    const warning = maybeError?.response?.status < 500;

    if (!config.isProduction) {
      // eslint-disable-next-line no-console
      console[warning ? 'warn' : 'error'](type, errorCode, maybeError, rest);
    }

    if (process.env.NODE_ENV === 'production' && action.type !== REDUX_FORM_SUBMIT_FAILED_ACTION) {
      const error =
        maybeError instanceof Error
          ? maybeError
          : new Error(
              typeof maybeError === 'string'
                ? maybeError
                : `Unknown exception: ${JSON.stringify(maybeError)}`
            );
      error.name = `${action.type}${error.name ? `: ${error.name}` : ''}`;
      Sentry.captureException(error, {
        level: warning ? 'warning' : 'error',
        context: {
          requestUrl: error?.response?.url,
          requestStatusCode: error?.response?.status,
        },
      });
    }
  }
  next(action);
};

export default (initialState = {}, routerHistory) => {
  const middlewares = [
    sentryReduxMiddleware({
      configureScopeWithState: (scope, state) => {
        const { user } = state.get('entities');
        if (!user) {
          return;
        }
        const userContext = {
          id: user.id,
        };
        if (user.organization) {
          const isCompany = user.organization.isCompany();
          userContext[isCompany ? 'company' : 'nonprofit'] = user.organization.name;
          userContext[isCompany ? 'companyId' : 'nonprofitId'] = user.organization.id;
        }
        scope.setUser(userContext);
      },
      stateTransformer: (state) => {
        const { auth, entities, ...cleanedState } = state.toJS();
        return cleanedState;
      },
    }),
    logActions,
    epicMiddleware,
    logErrors,
    routerMiddleware(routerHistory),
    metricsMiddleware,
  ];

  const enhancers = [applyMiddleware(...middlewares)];

  // If Redux DevTools Extension is installed use it, otherwise use Redux compose
  /* eslint-disable no-underscore-dangle */
  const composeEnhancers =
    process.env.NODE_ENV !== 'production' &&
    typeof window === 'object' &&
    window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__
      ? window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__
      : compose;
  /* eslint-enable */

  const store = createStore(createReducer(), fromJS(initialState), composeEnhancers(...enhancers));

  const authTokenOnly = createFilter('entities', ['auth']);

  persistStore(store, {
    whitelist: ['entities'],
    storage: asyncLocalStorage,
    records: [Auth],
    transforms: [authTokenOnly],
  });

  // Extensions
  store.injectedReducers = {};
  store.injectedEpics = {};
  store.rootEpic = epic$;

  // if (!config.isProduction) {
  //   // @NOTE-DP: a debugging helper that allows you to grab Redux state at any time you desire.
  //   // Useful for applying it later in storybook
  //   window.dumpRedactedReduxState = () => {
  //     // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-assignment
  //     const serializedState = serialize(redactData(store.getState()));
  //     // eslint-disable-next-line no-console
  //     console.log(serializedState);
  //     if (window.copy) {
  //       window.copy(serializedState);
  //     }
  //     return serializedState;
  //   };
  //   window.getReduxState = () => store.getState();
  // }

  // Make reducers hot reloadable, see http://mxs.is/googmo
  /* istanbul ignore next */
  if (import.meta.hot) {
    import.meta.hot.accept('./reducers', (newModule) => {
      if (newModule) {
        const createReducers = newModule.default;
        const nextReducers = createReducers(store.injectedReducers);

        store.replaceReducer(nextReducers);
      }
    });
  }

  return store;
};
